/**
 * VerifyEmailBanner — soft-blocking banner shown at the top of the app
 * shell when the signed-in user hasn't verified their email yet.
 *
 * Behavior:
 *   - Hidden when no user, or `user.email_verified === true`.
 *   - "Resend email" button hits /api/auth/email/verify/send.
 *   - Banner is dismissable for the session (sessionStorage flag) so the
 *     user can hide it temporarily, but it comes back on next reload —
 *     verification IS important.
 */
import { useState } from "react";
import { Mail, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const DISMISS_KEY = "bloq.verify_banner.dismissed";

export default function VerifyEmailBanner() {
  const { user, refreshUser } = useAuth();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === "1"; }
    catch { return false; }
  });
  const [busy, setBusy] = useState(false);

  // Don't render for guests, OAuth users (Google verifies for us), or
  // already-verified accounts.
  if (!user) return null;
  if (user.email_verified) return null;
  if (dismissed) return null;

  const onResend = async () => {
    setBusy(true);
    try {
      await api.post("/auth/email/verify/send", { email: user.email });
      // Self-heal: the backend silently returns 200 for already-verified
      // users (anti-enumeration). After every resend click, pull a fresh
      // /me so if the user IS already verified (e.g. they just clicked
      // their verify link in another tab, or a Google login earlier
      // upgraded them), the banner disappears on its own. Failure of
      // refreshUser doesn't change the outcome — banner stays until next
      // reload.
      try { await refreshUser?.(); } catch { /* ignore */ }
      toast.success("Verification email sent — check your inbox");
    } catch (e) {
      toast.error(formatApiError(e) || "Could not resend verification email");
    } finally {
      setBusy(false);
    }
  };
  const onDismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      className="bg-[#fff5e6] border-b border-[rgba(180,83,9,0.2)]"
      data-testid="verify-email-banner"
    >
      <div className="bloq-container py-2 flex items-center gap-3 text-sm">
        <Mail className="w-4 h-4 text-[#b45309] flex-shrink-0" />
        <p className="flex-1 text-[#7c3a06]">
          <strong>Verify your email.</strong> We sent a link to{" "}
          <strong>{user.email}</strong>. Some features stay locked until you confirm.
        </p>
        <button
          onClick={onResend}
          disabled={busy}
          className="text-xs px-3 py-1.5 bloq-btn-secondary flex-shrink-0"
          data-testid="verify-banner-resend"
        >
          {busy ? "Sending…" : "Resend email"}
        </button>
        <button
          onClick={onDismiss}
          className="w-7 h-7 grid place-items-center hover:bg-[rgba(180,83,9,0.1)] rounded flex-shrink-0"
          aria-label="Dismiss"
          data-testid="verify-banner-dismiss"
        >
          <X className="w-3.5 h-3.5 text-[#7c3a06]" />
        </button>
      </div>
    </div>
  );
}
